/**
 * The top search's grouping (PR 6a): Clients, Staff and the Directory, each
 * matched by the one rule the Clients page uses (src/lib/nameSearch.ts),
 * each over its own name fields only, and each capped at
 * GLOBAL_SEARCH_PER_GROUP rows. Pure: the panel fetches, this decides.
 *
 * - Clients match on the client's own display name, never a care
 *   manager's, so a staff name typed in does not list someone's caseload.
 *   The read returns active clients only; archived clients are found on
 *   the Clients page's archived view.
 * - Staff match on full and preferred name.
 * - The Directory is searched by the server (GET /providers/search), as
 *   the Directory page searches.
 */

import { matchesSearch, searchQuery } from './nameSearch';
import type { ClientRosterRowWire } from './clientsApi';
import { roleLine, type TeamDirectoryPerson } from './teamDirectory';
import type { SearchResult } from './types';

/** Rows shown per group. */
export const GLOBAL_SEARCH_PER_GROUP = 5;
/** Characters before any group is searched. */
export const GLOBAL_SEARCH_MIN_CHARS = 2;

export type GlobalSearchKind = 'client' | 'staff' | 'directory';

export interface GlobalSearchRow {
  kind: GlobalSearchKind;
  id: string;
  label: string;
  /** A second line, or null. */
  detail: string | null;
  href: string;
}

/** The fields a client offers the top search: its own name only. */
export function topSearchClientFields(c: Pick<ClientRosterRowWire, 'display_name'>): (string | null)[] {
  return [c.display_name];
}

export function matchClients(
  clients: readonly ClientRosterRowWire[],
  rawQuery: string,
  tenantSlug: string,
): GlobalSearchRow[] {
  const q = searchQuery(rawQuery);
  return clients
    .filter((c) => matchesSearch(q, topSearchClientFields(c)))
    .slice(0, GLOBAL_SEARCH_PER_GROUP)
    .map((c) => ({
      kind: 'client',
      id: c.id,
      label: c.display_name,
      detail: c.is_demo ? 'Demo client' : null,
      href: `/t/${tenantSlug}/clients/${c.id}`,
    }));
}

export function matchStaff(
  people: readonly TeamDirectoryPerson[],
  rawQuery: string,
  tenantSlug: string,
  canOpenCareManager: boolean,
): GlobalSearchRow[] {
  const q = searchQuery(rawQuery);
  return people
    .filter((p) => matchesSearch(q, [p.full_name, p.preferred_name]))
    .slice(0, GLOBAL_SEARCH_PER_GROUP)
    .map((p) => ({
      kind: 'staff',
      id: p.roster_id,
      label: p.full_name,
      detail: roleLine(p),
      href:
        p.does_care_management && canOpenCareManager
          ? `/t/${tenantSlug}/care-managers/${p.roster_id}`
          : `/t/${tenantSlug}/team?tab=directory`,
    }));
}

export function directoryRows(rows: readonly SearchResult[], tenantSlug: string): GlobalSearchRow[] {
  return rows.slice(0, GLOBAL_SEARCH_PER_GROUP).map((r) => ({
    kind: 'directory',
    id: r.id,
    label: r.display_name,
    detail: r.type === 'organization' ? 'Organization' : 'Individual',
    href: r.type === 'organization' ? `/t/${tenantSlug}/organizations/${r.id}` : `/t/${tenantSlug}/individuals/${r.id}`,
  }));
}

/** The live region's sentence once the results settle. */
export function resultsSentence(counts: { client?: number; staff?: number; directory?: number }): string {
  const parts: string[] = [];
  if (counts.client !== undefined) parts.push(`${counts.client} ${counts.client === 1 ? 'client' : 'clients'}`);
  if (counts.staff !== undefined) parts.push(`${counts.staff} staff`);
  if (counts.directory !== undefined) parts.push(`${counts.directory} in the Directory`);
  const total = (counts.client ?? 0) + (counts.staff ?? 0) + (counts.directory ?? 0);
  return total === 0 ? 'No matches' : parts.join(', ');
}
